// The Excel report's 7 work sheets (Set-Up, Borderspray, Application, Assessment, Soil_Sample, Pegup, Harvest): which
// trials of sheet 1 each one lists, and with which columns - the customer's Work_Sorting.py
// (Documentation/excel-report), rule for rule as it actually behaves, with two of its faults left out:
// - an empty cell counts as "" (the script expects "" and fails or misreads None);
// - a real date counts as a date (its is_date only knows text dates, l.12-24).
// A cell value is sheet 1's (baseSheet.js): a Date = done, a string, or null = empty. Line numbers (l.) are
// Work_Sorting.py's. A rule gets a trial's values and its sheet 1 row (baseRow: e.g. which activities it has).
import { COL } from './baseSheet';
import { cellStatus } from './cellStatus';
import { nextTaskIndex } from './cellStyle';
import { ACTIVITY_VANOS, APPLICATION_LETTERS, VISIT_NUMBERS, isDone } from './taskCells';

/** Initials whose trials no work sheet lists (l.69: "KF" in PI, FT or SD). */
export const EXCLUDED_INITIALS = ['KF'];
/** The Area (trial type) whose trials no work sheet lists (l.69). */
export const EXCLUDED_AREA = 'GLP';

// A cell as the script reads it: "" when empty, text otherwise (a date is never compared as text below)
const text = (value) => (value === null || value === undefined ? '' : String(value));
const strip = (value) => text(value).trim();

/** The columns of sheet 1 every work sheet starts with (l.43), by sheet 1's 0-based index. */
export const COMMON_COLUMNS = [0, 1, 3, 22, 4, 5, 6, 20, 7, 9, 11, 12, 13, 18, 19, 21];
const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
const A_TO_L = range(COL.A, COL.L); // 23-34
const V00_TO_V14 = range(COL.V00, COL.V14); // 35-49

/**
 * Whether any work sheet may list the trial (l.69): none of PI, FT, SD is "KF", Area is not "GLP", and Pegs up is
 * not done.
 */
export function onWorkSheets(values) {
  const people = [values[COL.PI], values[COL.FT], values[COL.SD]].filter((v) => v !== null && v !== undefined);
  return people.every((v) => !EXCLUDED_INITIALS.includes(strip(v)))
    && values[COL.Area] !== EXCLUDED_AREA
    && !isDone(values[COL['Pegs up']]);
}

/**
 * The script's loop over a run of task cells, last to first (l.91-98 for A-L, l.104-116 for V00-V14): the result is
 * decided by the FIRST cell that is not done - true when it holds text, false when it is empty; false when all are
 * done.
 */
function firstOpenHasText(values, indexes) {
  const open = indexes.find((i) => !isDone(values[i]));
  return open !== undefined && text(values[open]) !== '';
}

/**
 * The Assessment loop over V14 down to V00 (l.104-116), as it behaves: V01-V14 decide as firstOpenHasText; then
 * V00 - done: that stays; "?": the loop stops there and that stays; anything else, empty included: true.
 */
function assessmentLoop(values) {
  const fromV01 = firstOpenHasText(values, V00_TO_V14.slice(1));
  const v00 = values[COL.V00];
  if (isDone(v00) || strip(v00) === '?') return fromV01;
  return true;
}

// A work sheet's own tasks, in order, of a trial's tasks by column (tasksByColumn): { code, name, header (its column
// on the sheet), task }. Its next task (sheetNextTask) is framed black.
const activityRun = (vano, name) => (byColumn) => [{ code: vano, name, header: name, task: byColumn.activities[vano] }];
const applicationRun = (byColumn) => APPLICATION_LETTERS.map((code) => ({ code, name: 'Application', header: code, task: byColumn.applications[code] }));
const assessmentRun = (byColumn) => VISIT_NUMBERS.map((code) => ({ code, name: 'Assessment', header: code, task: byColumn.visits[code] }));

/**
 * Each work sheet: its name, its extra columns after the common ones (l.46-63), which trials it lists (l.73-150), and
 * its own tasks, whose next one is framed (`nextTask`: their run; none on Soil_Sample and Harvest, which have no task
 * column of their own - Harvest's is the flag).
 */
export const WORK_SHEETS = [
  {
    name: 'Set-Up',
    extra: [COL.A, COL.B, COL.C], // l.48
    includes: (v) => !isDone(v[COL['Stacked out']]), // l.73-78: Stacked out not done
    nextTask: { run: activityRun(ACTIVITY_VANOS.stackedOut, 'Stacked out') },
  },
  {
    name: 'Borderspray',
    extra: [COL['Borders sprayed']], // l.51
    nextTask: { run: activityRun(ACTIVITY_VANOS.bordersSprayed, 'Borders sprayed') },
    // l.80-87: Borders sprayed not done, and not "Not needed" - and, unlike the script, only a trial that has the
    // task (an empty cell can be a task with no dates yet, which stays in)
    includes: (v, trial) => trial.activities.includes(ACTIVITY_VANOS.bordersSprayed)
      && !isDone(v[COL['Borders sprayed']]) && strip(v[COL['Borders sprayed']]).toLowerCase() !== 'not needed',
  },
  {
    name: 'Application',
    extra: A_TO_L, // l.54
    // The template's columns prod, papir, person after FT, filled in by hand (not in the script)
    manualColumns: { after: 'FT', headers: ['prod', 'papir', 'person'] },
    includes: (v) => firstOpenHasText(v, A_TO_L), // l.89-100: the first application not done holds text
    nextTask: { run: applicationRun },
  },
  {
    name: 'Assessment',
    extra: [...V00_TO_V14, COL.A, COL.B, COL.C], // l.57
    includes: (v) => assessmentLoop(v), // l.102-118
    nextTask: { run: assessmentRun },
  },
  {
    name: 'Soil_Sample',
    extra: [], // no extra columns
    // l.120-122: Trialaddress filled in and EarthSample empty
    includes: (v) => text(v[COL.Trialaddress]) !== '' && text(v[COL.EarthSample]) === '',
  },
  {
    name: 'Pegup',
    extra: [COL['Pegs up'], COL.Harvest], // l.60
    // l.124-144: the same loop as Assessment, and listed when it says false - every assessment done (or the first
    // one not done is empty); V00 "?" leaves it to V01-V14. A Pegs up task is not required.
    includes: (v) => !assessmentLoop(v),
    nextTask: { run: activityRun(ACTIVITY_VANOS.pegsUp, 'Pegs up') },
  },
  {
    name: 'Harvest',
    extra: [COL['Pegs up'], COL.Harvest], // l.63
    includes: (v) => v[COL.Harvest] === true, // l.146-150: bool(Harvest) - the flag is TRUE
  },
];

/** A work sheet's headers: sheet 1's for its columns, with the manual columns where the template has them. */
export function workSheetHeaders(sheet, baseHeaders) {
  const headers = [...COMMON_COLUMNS, ...sheet.extra].map((i) => baseHeaders[i]);
  if (!sheet.manualColumns) return headers;
  const at = headers.indexOf(sheet.manualColumns.after) + 1;
  return [...headers.slice(0, at), ...sheet.manualColumns.headers, ...headers.slice(at)];
}

/** A listed trial's row on a work sheet: sheet 1's values of its columns, the manual columns empty. */
export function workSheetRow(sheet, values, baseHeaders) {
  const row = [...COMMON_COLUMNS, ...sheet.extra].map((i) => values[i] ?? null);
  if (!sheet.manualColumns) return row;
  const at = [...COMMON_COLUMNS, ...sheet.extra].map((i) => baseHeaders[i]).indexOf(sheet.manualColumns.after) + 1;
  return [...row.slice(0, at), ...sheet.manualColumns.headers.map(() => null), ...row.slice(at)];
}

/**
 * A work sheet's next task for a trial (its tasks by column) on `exportDate`: of the sheet's own tasks, the first open
 * one after the last done one (nextTaskIndex) - { code, name, header, task, status } - or null (none open, or a sheet
 * without its own tasks).
 */
export function sheetNextTask(sheet, byColumn, exportDate) {
  if (!sheet.nextTask) return null;
  const run = sheet.nextTask.run(byColumn);
  const statuses = run.map(({ task }) => (task ? cellStatus(task, exportDate) : null));
  const i = nextTaskIndex(run.map((_, n) => n), statuses);
  return i === null ? null : { ...run[i], status: statuses[i] };
}

/** The rows each work sheet lists, in sheet 1's order: { 'Set-Up': [baseRow, ...], ... }, of sheet 1's rows (baseRow). */
export function workSheetSelection(baseRows) {
  const eligible = baseRows.filter((trial) => onWorkSheets(trial.values));
  return Object.fromEntries(WORK_SHEETS.map((sheet) => [sheet.name, eligible.filter((trial) => sheet.includes(trial.values, trial))]));
}
